import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { ChartDimensions, PaddingConfig } from '../../types';
import { mergePadding, defaultPadding } from '../../utils/defaults';
import { generateId } from '../../utils/svg';

interface ChartContainerProps {
  width?: string | number;
  height?: string | number;
  minHeight?: number;
  padding?: PaddingConfig | number;
  backgroundColor?: string;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  title?: string;
  children: (
    dimensions: ChartDimensions, 
    svgRef: React.RefObject<SVGSVGElement>,
    isMobile: boolean
  ) => React.ReactNode;
}

// Breakpoints for responsive design
const MOBILE_BREAKPOINT = 480;
const TABLET_BREAKPOINT = 768;

/**
 * ChartContainer - Responsive SVG container that calculates dimensions
 * Follows WCAG 2.1 accessibility guidelines
 */
export const ChartContainer: React.FC<ChartContainerProps> = ({
  width = '100%',
  height = 300,
  minHeight = 200,
  padding,
  backgroundColor,
  className,
  style,
  id,
  ariaLabel,
  ariaDescribedBy,
  title,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  const chartId = useMemo(() => id || generateId('chart'), [id]);
  const descId = useMemo(() => `${chartId}-desc`, [chartId]);

  // Responsive padding based on screen size
  // Extra padding for title and legend
  const hasTitle = !!title;
  
  const responsivePadding = useMemo(() => {
    const basePadding = mergePadding(padding, defaultPadding);
    
    // Add extra top padding if there's a title
    const titleOffset = hasTitle ? 25 : 0;
    // Add extra bottom padding for legend
    const legendOffset = 25;
    
    if (isMobile) {
      return {
        top: Math.max(20, basePadding.top * 0.6) + (hasTitle ? 15 : 0),
        right: Math.max(10, basePadding.right * 0.5),
        bottom: Math.max(40, basePadding.bottom * 0.8) + 15,
        left: Math.max(35, basePadding.left * 0.7),
      };
    }
    
    if (isTablet) {
      return {
        top: Math.max(25, basePadding.top * 0.8) + (hasTitle ? 18 : 0),
        right: Math.max(15, basePadding.right * 0.7),
        bottom: Math.max(45, basePadding.bottom * 0.9) + 18,
        left: Math.max(40, basePadding.left * 0.85),
      };
    }
    
    return {
      top: basePadding.top + titleOffset,
      right: basePadding.right,
      bottom: basePadding.bottom + legendOffset,
      left: basePadding.left,
    };
  }, [padding, isMobile, isTablet, hasTitle]);

  // Handle responsive sizing
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const computedHeight = typeof height === 'number' ? height : rect.height;
        
        // Responsive height adjustment
        let adjustedHeight = computedHeight;
        if (rect.width < MOBILE_BREAKPOINT) {
          adjustedHeight = Math.max(minHeight, computedHeight * 0.8);
        }
        
        setContainerSize({
          width: rect.width,
          height: Math.max(minHeight, adjustedHeight),
        });
        
        setIsMobile(rect.width < MOBILE_BREAKPOINT);
        setIsTablet(rect.width >= MOBILE_BREAKPOINT && rect.width < TABLET_BREAKPOINT);
      }
    };

    updateSize();
    
    // Use ResizeObserver for better performance
    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also listen to window resize for orientation changes
    window.addEventListener('resize', updateSize);
    window.addEventListener('orientationchange', updateSize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSize);
      window.removeEventListener('orientationchange', updateSize);
    };
  }, [height, minHeight]);

  // Calculate dimensions
  const dimensions: ChartDimensions = useMemo(() => {
    const w = typeof width === 'number' ? width : containerSize.width;
    const h = typeof height === 'number' ? Math.max(minHeight, height) : containerSize.height;
    
    return {
      width: w,
      height: h,
      chartWidth: Math.max(0, w - responsivePadding.left - responsivePadding.right),
      chartHeight: Math.max(0, h - responsivePadding.top - responsivePadding.bottom),
      padding: responsivePadding,
    };
  }, [containerSize, width, height, minHeight, responsivePadding]);

  // Container styles
  const containerStyle: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${Math.max(minHeight, height)}px` : height,
    minHeight: `${minHeight}px`,
    backgroundColor,
    position: 'relative',
    touchAction: 'pan-y', // Allow vertical scrolling on mobile
    ...style,
  };

  // Don't render until we have dimensions
  if (dimensions.width === 0) {
    return (
      <div 
        ref={containerRef} 
        className={className}
        style={containerStyle}
        id={chartId}
        role="img"
        aria-label={ariaLabel || 'Chart loading...'}
      />
    );
  }

  return (
    <div 
      ref={containerRef} 
      className={className}
      style={containerStyle}
      id={chartId}
    >
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={title || ariaLabel || 'Data visualization chart'}
        aria-describedby={ariaDescribedBy || descId}
        style={{ 
          display: 'block', 
          overflow: 'visible',
          maxWidth: '100%',
          height: 'auto',
        }}
        tabIndex={0}
      >
        {/* Accessibility: Hidden description (no title to avoid browser tooltip) */}
        <desc id={descId}>
          {ariaLabel || title || 'Interactive bar chart displaying data values'}
        </desc>
        
        {/* Background */}
        {backgroundColor && (
          <rect
            x={0}
            y={0}
            width={dimensions.width}
            height={dimensions.height}
            fill={backgroundColor}
            aria-hidden="true"
          />
        )}
        
        {/* Chart content */}
        <g transform={`translate(${responsivePadding.left}, ${responsivePadding.top})`}>
          {children(dimensions, svgRef, isMobile)}
        </g>
      </svg>
    </div>
  );
};

export default ChartContainer;
